import { Trans, useTranslation } from "react-i18next";
import { ArrowRight } from "lucide-react";
import { Button } from "~/components/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Alert } from "~/components/ui/alert";
import { Card } from "~/components/ui/card";
import { Icon, IconProps } from "~/components/icon/icon";
import { Text } from "~/components/text";

interface KeywordsPipelineInfoModalProps {
  dialog: {
    isOpen: boolean;
    onOpenChange: (open: boolean) => void;
  };
}

interface SectionProps {
  icon: IconProps["name"];
  title: string;
  children: React.ReactNode;
  gradientBar?: boolean;
}

const Section = ({
  icon,
  title,
  children,
  gradientBar = true,
}: SectionProps) => (
  <div className="flex flex-col mb-6 last:mb-2">
    <div className="flex items-center">
      <div className="bg-primary-50 rounded-full p-2">
        <Icon name={icon} iconClassName="text-primary" />
      </div>
      <Text as="span" variant="title" className="ml-2">
        {title}
      </Text>
    </div>
    <div className="flex flex-row text-muted-foreground">
      <div className="w-[38px] mt-4 pb-2 flex justify-center relative shrink-0">
        {gradientBar && (
          <span className="w-[2px] h-full bg-gradient-to-b from-primary-300 to-white" />
        )}
      </div>
      <div className="flex flex-col flex-1">{children}</div>
    </div>
  </div>
);

interface BulletPointProps {
  title: string;
  description: string;
}

const BulletPoint = ({ title, description }: BulletPointProps) => (
  <Card className="flex flex-row items-center gap-2 px-2 py-4 shadow-md">
    <Icon name="circle-check" size="large" iconClassName="text-primary ml-4" />
    <div className="flex flex-col ml-4 text-muted-foreground">
      <Text variant="title">{title}</Text>
      <Text>{description}</Text>
    </div>
  </Card>
);

export const KeywordsPipelineInfoModal = ({
  dialog,
}: KeywordsPipelineInfoModalProps) => {
  const { t } = useTranslation(["components"]);

  return (
    <Dialog open={dialog.isOpen} onOpenChange={dialog.onOpenChange}>
      <DialogContent className="lg:min-w-[670px] px-8 py-6 max-h-[90vh] flex flex-col">
        <DialogHeader className="items-start shrink-0">
          <DialogTitle>
            {t("components:keywordsTab.keywordsPipelineInfoModal.title")}
          </DialogTitle>
          <DialogDescription />
        </DialogHeader>

        <div className="flex flex-col overflow-y-auto">
          <Section
            icon="search"
            title={t(
              "components:keywordsTab.keywordsPipelineInfoModal.context.title",
            )}
          >
            <Card className="flex flex-col gap-2 p-4 mt-2 shadow-md">
              <Text variant="metric">
                <Trans
                  i18nKey="components:keywordsTab.keywordsPipelineInfoModal.context.description"
                  components={{
                    strong: (
                      <strong className="font-semibold text-foreground" />
                    ),
                  }}
                />
              </Text>
              <Alert className="bg-system-error/5 border-system-error/20 flex items-center gap-2 text-system-error">
                <Icon name="triangle-alert" />
                <Text as="span" variant="errorSmall">
                  {t(
                    "components:keywordsTab.keywordsPipelineInfoModal.context.warning",
                  )}
                </Text>
              </Alert>
            </Card>
          </Section>

          <Section
            icon="funnel"
            title={t(
              "components:keywordsTab.keywordsPipelineInfoModal.focus.title",
            )}
          >
            <Text variant="metric" className="ml-1">
              {t(
                "components:keywordsTab.keywordsPipelineInfoModal.focus.description",
              )}
            </Text>
            <div className="flex flex-col gap-2 mt-4">
              <BulletPoint
                title={t(
                  "components:keywordsTab.keywordsPipelineInfoModal.focus.maximising.title",
                )}
                description={t(
                  "components:keywordsTab.keywordsPipelineInfoModal.focus.maximising.description",
                )}
              />
              <BulletPoint
                title={t(
                  "components:keywordsTab.keywordsPipelineInfoModal.focus.redundancy.title",
                )}
                description={t(
                  "components:keywordsTab.keywordsPipelineInfoModal.focus.redundancy.description",
                )}
              />
            </div>
            <Text variant="metricItalic" className="mt-4">
              {t(
                "components:keywordsTab.keywordsPipelineInfoModal.focus.combination",
              )}
            </Text>
          </Section>

          <Section
            icon="rocket"
            title={t(
              "components:keywordsTab.keywordsPipelineInfoModal.result.title",
            )}
            gradientBar={false}
          >
            <Card className="flex flex-row items-center gap-4 p-4 w-full justify-between mt-2 border-primary-200 bg-gradient-to-r from-white to-primary-50 shadow-md">
              <div className="flex flex-col px-8">
                <Text as="span" variant="title">
                  {t(
                    "components:keywordsTab.keywordsPipelineInfoModal.result.moreWords",
                  )}
                </Text>
                <Text as="span" variant="caption">
                  {t(
                    "components:keywordsTab.keywordsPipelineInfoModal.result.moreWordsDescription",
                  )}
                </Text>
              </div>
              <ArrowRight className="w-5 h-5 text-primary-500 shrink-0" />
              <div className="flex flex-col px-8">
                <Text as="span" variant="link">
                  {t(
                    "components:keywordsTab.keywordsPipelineInfoModal.result.higherReach",
                  )}
                </Text>
                <Text as="span" variant="caption">
                  {t(
                    "components:keywordsTab.keywordsPipelineInfoModal.result.higherReachDescription",
                  )}
                </Text>
              </div>
            </Card>
          </Section>
        </div>

        <DialogFooter className="shrink-0">
          <DialogClose asChild>
            <Button
              label={t(
                "components:keywordsTab.keywordsPipelineInfoModal.close",
              )}
              variant="primary"
              size="small"
            />
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
